/**
 * Unified Migrations card (replaces the Batch A/C/D token cards).
 *
 * - Always covers ALL migrations: backend discovers the migrations
 *   directory vs the tracker, so new migrations appear here with no UI
 *   changes ever needed.
 * - "Preview changes": read-only, plain-language sentence per pending
 *   migration with live record counts.
 * - "Apply all pending": super-admin session + typed confirmation word
 *   ("APPLY"). No per-batch tokens — those are retired (legacy view
 *   keeps the old batch records).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Loader2, RefreshCw, Database, ChevronDown, ChevronRight,
  CheckCircle2, AlertOctagon, Eye, PlayCircle, FileSearch, RotateCcw, Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const RESULT_BADGE = {
  applied:         'bg-emerald-100 text-emerald-900 border-emerald-300',
  already_applied: 'bg-slate-100 text-slate-600 border-slate-300',
  failed:          'bg-rose-100 text-rose-900 border-rose-300',
};

export default function MigrationsCard({ onAfterApply }) {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showAllApplied, setShowAllApplied] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  // 2026-02 safety amendment — per-migration single-apply flow. Each
  // pending row can expand an inline typed-confirmation panel that POSTs
  // {name, confirm: "APPLY"} so neighbours are NOT triggered.
  const [singleTarget, setSingleTarget] = useState(null);   // migration name
  const [singleConfirm, setSingleConfirm] = useState('');
  const [singleLoading, setSingleLoading] = useState(false);

  const fetchPreview = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/preview`, { headers: authHeaders() });
      setPreview(res.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      if (!silent) setPreviewLoading(false);
    }
  }, []);

  // Light status fetch on mount so the header badge is live without a click.
  useEffect(() => { fetchPreview({ silent: true }); }, [fetchPreview]);

  const pendingCount = preview?.totals?.pending ?? null;
  const confirmWord = preview?.confirm_word || 'APPLY';
  const pendingItems = (preview?.items || []).filter((i) => i.status === 'pending');
  const appliedItems = (preview?.items || []).filter((i) => i.status === 'applied');

  // 2026-02 safety amendment — apply ONE named migration. The backend
  // shares the endpoint but restricts the pending list to just `name`
  // when it's passed. Neighbours (older or newer pending migrations)
  // are NOT touched. Same typed-APPLY confirmation.
  const handleApplyOne = async (name) => {
    setSingleLoading(true);
    setApplyResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/apply`,
        { name, confirm: singleConfirm.trim() },
        { headers: authHeaders() },
      );
      setApplyResult(res.data);
      setSingleConfirm('');
      setSingleTarget(null);
      const t = res.data?.totals || {};
      if (res.data?.stopped_early) {
        toast.error(`${name} failed`);
      } else if (t.applied > 0) {
        toast.success(`${name} applied (neighbours skipped)`);
      } else {
        toast.info(`${name}: nothing changed (already applied?)`);
      }
      await fetchPreview({ silent: true });
      if (onAfterApply) onAfterApply();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Single-apply failed');
    } finally {
      setSingleLoading(false);
    }
  };

  const handleApply = async () => {
    setApplyLoading(true);
    setApplyResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/apply`,
        { confirm: confirmText.trim() },
        { headers: authHeaders() },
      );
      setApplyResult(res.data);
      setConfirmText('');
      const t = res.data?.totals || {};
      if (res.data?.stopped_early) {
        toast.error(`Stopped early — ${t.applied} applied, ${t.failed} failed`);
      } else if (t.applied > 0) {
        toast.success(`${t.applied} migration${t.applied === 1 ? '' : 's'} applied`);
      } else {
        toast.info('Nothing was pending — already up to date');
      }
      await fetchPreview({ silent: true });
      if (onAfterApply) onAfterApply();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyLoading(false);
    }
  };

  const fetchDiag = async () => {
    setDiagLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/migrations/diag`, { headers: authHeaders() });
      setDiag(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diag fetch failed');
    } finally {
      setDiagLoading(false);
    }
  };

  const handleReset = async () => {
    if (!resetTarget) {
      toast.error('Pick a migration to reset from the list below');
      return;
    }
    const expected = `RESET ${resetTarget}`;
    if (resetConfirm !== expected) {
      toast.error(`Type exactly: ${expected}`);
      return;
    }
    setResetLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/migrations/reset`,
        { name: resetTarget, confirm: resetConfirm },
        { headers: authHeaders() },
      );
      toast.success(`Tracker row deleted (${res.data.deleted_count}) — ${resetTarget} is now PENDING`);
      setResetTarget('');
      setResetConfirm('');
      await fetchPreview({ silent: true });
      await fetchDiag();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Card className="mb-4 border-indigo-200" data-testid="migrations-card">
      <CardHeader className="cursor-pointer" onClick={() => setExpanded((v) => !v)} data-testid="migrations-card-toggle">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
            <Database className="w-5 h-5 text-indigo-600" />
            <CardTitle className="text-lg">Migrations</CardTitle>
          </div>
          {pendingCount !== null && (
            <Badge
              variant="outline"
              className={pendingCount > 0
                ? 'bg-amber-100 text-amber-900 border-amber-300'
                : 'bg-emerald-100 text-emerald-900 border-emerald-300'}
              data-testid="migrations-pending-badge"
            >
              {pendingCount > 0 ? `${pendingCount} pending` : 'Up to date'}
            </Badge>
          )}
        </div>
        <CardDescription>
          One runner for ALL database migrations — current and future. New migrations appear
          here automatically. Super-admin only; applying asks for a typed confirmation
          (the old per-batch tokens are retired).
        </CardDescription>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => fetchPreview()}
              disabled={previewLoading}
              data-testid="migrations-preview-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview changes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchPreview({ silent: false })}
              disabled={previewLoading}
              title="Refresh status"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Pending list — plain language */}
          {preview && (
            <div data-testid="migrations-preview-panel">
              {pendingItems.length === 0 ? (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Nothing pending — all {preview.totals.discovered} migrations are applied.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-slate-700">
                    {pendingItems.length} pending migration{pendingItems.length === 1 ? '' : 's'} — what each one will do:
                  </div>
                  {pendingItems.map((item) => {
                    const isTarget = singleTarget === item.name;
                    return (
                      <div
                        key={item.name}
                        className="p-3 rounded-lg border border-amber-200 bg-amber-50"
                        data-testid={`migrations-pending-${item.number}`}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="bg-white text-amber-900 border-amber-300 font-mono text-[11px]">
                            {item.number}
                          </Badge>
                          <code className="text-xs text-slate-600 flex-1 min-w-[200px]">{item.name}</code>
                          {!isTarget && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[11px] h-7 bg-white"
                              onClick={() => {
                                setSingleTarget(item.name);
                                setSingleConfirm('');
                                setApplyResult(null);
                              }}
                              data-testid={`migrations-apply-only-btn-${item.number}`}
                              title={`Apply ONLY ${item.name} — neighbouring pending migrations will NOT run`}
                            >
                              <PlayCircle className="w-3.5 h-3.5 mr-1" />
                              Apply only this
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{item.plain_summary}</p>
                        {isTarget && (
                          <div
                            className="mt-3 p-3 rounded-lg border border-indigo-300 bg-white space-y-2"
                            data-testid={`migrations-apply-only-panel-${item.number}`}
                          >
                            <div className="text-xs text-slate-700">
                              Applying <code className="font-mono">{item.name}</code> only.
                              Other pending migrations ({pendingItems.filter((p) => p.name !== item.name).map((p) => p.number).join(', ') || 'none'})
                              will NOT be triggered. Type{' '}
                              <strong className="font-mono">{confirmWord}</strong> to confirm.
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Input
                                value={singleConfirm}
                                onChange={(e) => setSingleConfirm(e.target.value)}
                                placeholder={confirmWord}
                                className="w-40 font-mono"
                                data-testid={`migrations-apply-only-confirm-${item.number}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={() => handleApplyOne(item.name)}
                                disabled={singleLoading || singleConfirm.trim() !== confirmWord}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                data-testid={`migrations-apply-only-run-${item.number}`}
                              >
                                {singleLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
                                Apply {item.number} only
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setSingleTarget(null); setSingleConfirm(''); }}
                                disabled={singleLoading}
                                data-testid={`migrations-apply-only-cancel-${item.number}`}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Applied history (collapsed by default) */}
              <button
                type="button"
                className="mt-3 text-xs text-slate-500 underline"
                onClick={() => setShowAllApplied((v) => !v)}
                data-testid="migrations-show-applied-toggle"
              >
                {showAllApplied ? 'Hide' : 'Show'} {appliedItems.length} applied migration{appliedItems.length === 1 ? '' : 's'}
              </button>
              {showAllApplied && (
                <div className="mt-2 max-h-56 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {appliedItems.map((item) => (
                    <div key={item.name} className="px-3 py-1.5 flex items-center justify-between text-xs">
                      <code className="text-slate-600">{item.name}</code>
                      <span className="text-slate-400">{item.applied_at ? item.applied_at.slice(0, 19).replace('T', ' ') : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Apply gate — typed confirmation word */}
          {preview && pendingItems.length > 0 && (
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2" data-testid="migrations-apply-panel">
              <div className="text-sm text-slate-700">
                Type <strong className="font-mono">{confirmWord}</strong> to run the {pendingItems.length} pending
                migration{pendingItems.length === 1 ? '' : 's'} in order. The runner stops at the first
                failure and is safe to re-run — finished steps are skipped.
              </div>
              <div className="flex flex-wrap gap-2">
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={confirmWord}
                  className="w-40 font-mono"
                  data-testid="migrations-confirm-input"
                />
                <Button
                  onClick={handleApply}
                  disabled={applyLoading || confirmText.trim() !== confirmWord}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="migrations-apply-btn"
                >
                  {applyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                  Apply all pending
                </Button>
              </div>
            </div>
          )}

          {/* Apply results */}
          {applyResult && (
            <div className="space-y-2" data-testid="migrations-apply-result">
              <div className={`p-3 rounded-lg border text-sm ${applyResult.stopped_early
                ? 'bg-rose-50 border-rose-200 text-rose-900'
                : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
              >
                <div className="flex items-center gap-2 font-semibold mb-1">
                  {applyResult.stopped_early ? <AlertOctagon className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                  {applyResult.stopped_early ? 'Stopped early' : 'Done'}
                  <span className="font-normal">
                    — {applyResult.totals.applied} applied · {applyResult.totals.failed} failed
                    {applyResult.totals.not_attempted_due_to_halt > 0 && ` · ${applyResult.totals.not_attempted_due_to_halt} not attempted`}
                  </span>
                </div>
                <div className="text-xs opacity-80">{applyResult.next_action}</div>
              </div>
              {(applyResult.results || []).map((r) => (
                <div key={r.name} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`${RESULT_BADGE[r.status] || ''} text-[11px]`}>
                      {r.status.replace('_', ' ')}
                    </Badge>
                    <code className="text-xs text-slate-600">{r.name}</code>
                    {typeof r.duration_seconds === 'number' && r.status === 'applied' && (
                      <span className="text-[11px] text-slate-400">{r.duration_seconds.toFixed(2)}s</span>
                    )}
                  </div>
                  {r.plain_summary && <p className="text-xs text-slate-600 mt-1">{r.plain_summary}</p>}
                  {r.error && <p className="text-xs text-rose-700 mt-1 font-mono">{r.error}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="text-xs text-slate-400">
            Records of the retired Batch A/C/D runs are preserved on the{' '}
            <a href="?legacy=1" className="underline">legacy view</a>.
          </div>

          {/* ------------------------------------------------------------
              Build 13.3 forensic diagnostics panel. Answers "what does
              THIS environment's runner actually see, and can I re-run
              a migration that no-op'd?" — MIGRATIONS_DIR path,
              per-file checksums, per-tracker-row applied_by, checksum
              drift, and orphan tracker rows (rows with no matching
              file). Also exposes a "Reset tracker row" flow so a
              super-admin can un-mark an over-eagerly-recorded
              migration (see 0047 story) and re-APPLY it cleanly.
              ------------------------------------------------------------ */}
          <div className="mt-4 p-3 rounded-lg border border-dashed border-slate-300 bg-white" data-testid="migrations-diag-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <FileSearch className="w-4 h-4 text-slate-500" />
                Runner diagnostics
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchDiag}
                disabled={diagLoading}
                data-testid="migrations-diag-fetch-btn"
              >
                {diagLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
                {diag ? 'Refresh dump' : 'Fetch diagnostic dump'}
              </Button>
              {diag && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    try {
                      const payload = JSON.stringify(diag, null, 2);
                      navigator.clipboard.writeText(payload).then(
                        () => toast.success('Diagnostic dump copied as JSON'),
                        () => toast.error('Clipboard write failed — select + copy manually'),
                      );
                    } catch (e) {
                      toast.error('Clipboard API unavailable');
                    }
                  }}
                  data-testid="migrations-diag-copy-btn"
                  className="ml-2"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" />
                  Copy dump as JSON
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-2">
              This shows exactly what the runner sees on THIS environment: the migrations directory it scans,
              every file it discovered (with checksum), every tracker row (with applied_by, applied_at, and
              checksum drift), and any pending/orphan rows. Read-only until you use the Reset button below.
            </p>

            {diag && (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div className="p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-slate-500 text-[11px] uppercase">Discovered</div>
                    <div className="font-mono text-slate-900">{diag.totals.discovered_files}</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-slate-500 text-[11px] uppercase">Tracker rows</div>
                    <div className="font-mono text-slate-900">{diag.totals.tracker_rows}</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-slate-500 text-[11px] uppercase">Pending</div>
                    <div className={`font-mono ${diag.totals.pending > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{diag.totals.pending}</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-slate-500 text-[11px] uppercase">Orphans</div>
                    <div className={`font-mono ${diag.totals.tracker_orphans > 0 ? 'text-rose-700' : 'text-slate-900'}`}>{diag.totals.tracker_orphans}</div>
                  </div>
                  <div className="p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-slate-500 text-[11px] uppercase">Drift</div>
                    <div className={`font-mono ${diag.totals.checksum_drift_rows > 0 ? 'text-rose-700' : 'text-slate-900'}`}>{diag.totals.checksum_drift_rows}</div>
                  </div>
                </div>

                <div className="p-2 rounded bg-slate-900 text-slate-200 font-mono text-[11px] break-all">
                  <div className="text-slate-400">MIGRATIONS_DIR:</div>
                  <div>{diag.migrations_dir}</div>
                </div>

                {/* Key-file check — quick answer to "is 0047 in this build?" */}
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Key file check</div>
                  {Object.entries(diag.key_files_check || {}).map(([name, info]) => (
                    <div key={name} className="flex items-center justify-between p-2 rounded bg-slate-50 border border-slate-200">
                      <code className="text-slate-700">{name}</code>
                      <div className="flex gap-2">
                        <Badge variant="outline" className={info.present_in_discovery ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-rose-50 text-rose-800 border-rose-300'}>
                          {info.present_in_discovery ? 'file present' : 'file MISSING'}
                        </Badge>
                        <Badge variant="outline" className={info.in_tracker_applied ? 'bg-slate-200 text-slate-700 border-slate-300' : 'bg-amber-50 text-amber-900 border-amber-300'}>
                          {info.in_tracker_applied ? 'tracker: applied' : 'tracker: pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>

                {diag.pending_names.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Pending — what each one will do</div>
                    <div className="space-y-2">
                      {(diag.pending_details || diag.pending_names.map((n) => ({name: n, plain_summary: '(no summary)'}))).map((p) => (
                        <div key={p.name} className="p-2 rounded bg-amber-50 border border-amber-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="bg-white text-amber-900 border-amber-300 font-mono text-[10px]">
                              {(p.name || '').split('_')[0]}
                            </Badge>
                            <code className="text-[11px] text-slate-700 flex-1">{p.name}</code>
                            {p.on_disk_checksum && <span className="text-[10px] text-slate-400 font-mono">{p.on_disk_checksum}</span>}
                          </div>
                          <p className="text-xs text-slate-800">{p.plain_summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {diag.tracker_orphans.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-rose-700 mb-1">Tracker orphans (row exists but file is gone — indicates a stale row)</div>
                    <div className="p-2 rounded bg-rose-50 border border-rose-200 font-mono">
                      {diag.tracker_orphans.map((n) => <div key={n}>{n}</div>)}
                    </div>
                  </div>
                )}

                <details className="border border-slate-200 rounded">
                  <summary className="cursor-pointer px-2 py-1 bg-slate-50 text-slate-700 select-none">
                    Full tracker rows ({diag.tracker.length})
                  </summary>
                  <div className="max-h-72 overflow-auto divide-y divide-slate-100">
                    {diag.tracker.map((r) => (
                      <div key={r.name} className="px-2 py-1.5 text-[11px]">
                        <div className="flex items-center gap-2">
                          <code className="text-slate-700 flex-1">{r.name}</code>
                          {r.checksum_drift && <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-300 text-[9px]">DRIFT</Badge>}
                          {!r.on_disk_file_exists && <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-300 text-[9px]">ORPHAN</Badge>}
                        </div>
                        <div className="text-slate-400 mt-0.5">
                          {r.applied_at ? r.applied_at.slice(0, 19).replace('T', ' ') : '(no applied_at)'}
                          {r.applied_by_email ? ` · by ${r.applied_by_email}` : ' · applied_by: (none — pre-guard row)'}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>

                <details className="border border-slate-200 rounded">
                  <summary className="cursor-pointer px-2 py-1 bg-slate-50 text-slate-700 select-none">
                    Full discovered files ({diag.discovered.length})
                  </summary>
                  <div className="max-h-72 overflow-auto divide-y divide-slate-100">
                    {diag.discovered.map((d) => (
                      <div key={d.name} className="px-2 py-1.5 text-[11px] flex items-center justify-between">
                        <code className="text-slate-700">{d.name}</code>
                        <span className="text-slate-400 font-mono">{d.on_disk_checksum} · {d.size_bytes} B</span>
                      </div>
                    ))}
                  </div>
                </details>

                {/* Reset tracker row */}
                <div className="mt-2 p-3 rounded border border-rose-200 bg-rose-50">
                  <div className="flex items-center gap-2 text-rose-900 text-sm font-semibold mb-1">
                    <RotateCcw className="w-4 h-4" /> Reset a tracker row (advanced)
                  </div>
                  <p className="text-xs text-rose-800 mb-2">
                    Removes ONE tracker row so its migration re-appears as PENDING. Use when a previous
                    apply recorded the row but the up() body no-op&apos;d against then-current state (e.g. an
                    integration wasn&apos;t configured yet). Re-applying is safe because migrations are
                    idempotent.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      value={resetTarget}
                      onChange={(e) => setResetTarget(e.target.value.trim())}
                      placeholder="0047_backfill_photo_thumbnails"
                      className="font-mono text-xs"
                      data-testid="migrations-reset-target"
                    />
                    <Input
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                      placeholder={resetTarget ? `RESET ${resetTarget}` : 'Type RESET <name>'}
                      className="font-mono text-xs"
                      data-testid="migrations-reset-confirm"
                    />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleReset}
                      disabled={resetLoading || !resetTarget || resetConfirm !== `RESET ${resetTarget}`}
                      data-testid="migrations-reset-btn"
                    >
                      {resetLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-2" />}
                      Reset tracker row
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
